// Página inicial (site público), na linguagem visual dos sites da DifLabs. Estilos em features/public/home.css.
import { Link } from 'react-router-dom'
import { ArrowRight, BarChart3, Bot, Check, Clock, Globe, LayoutTemplate, Lightbulb, LineChart, PiggyBank, Radar, ShieldCheck, Undo2, Wand2 } from 'lucide-react'
import { TRIAL_DAYS } from '@impulso/shared'
import { Faq } from '../../features/public/Site'
import { HeroFilm } from '../../features/public/HeroFilm'
import { Counter, Reveal, trackGlow, useInView, Words } from '../../features/public/motion'
import '../../features/public/home.css'

const PLATFORMS = ['Google Ads', 'Instagram', 'Facebook', 'TikTok', 'OpenAI Ads', 'Google Analytics', 'WhatsApp']

export default function Home() {
  return (
    <div className="sh">
      <Hero />
      <Marquee />
      <Problem />
      <Steps />
      <Features />
      <Numbers />
      <Agencies />
      <Questions />
      <FinalCta />
    </div>
  )
}

function Hero() {
  return (
    <section className="sh-hero">
      <div className="sh-glow" aria-hidden />
      <div className="sh-wrap sh-hero-grid">
        <div>
          <Reveal as="p" className="sh-label"><span className="sh-label-dot" /> Feito para pequenos negócios</Reveal>
          <Words as="h1" immediate delay={120} className="sh-display sh-h1" text="Seus anúncios no Google, Instagram e TikTok, _explicados_ e cuidados." />
          <Reveal as="p" delay={700} className="sh-lead">
            O Seu Impulso conecta suas contas, mostra em português claro quantos clientes cada real trouxe e diz o que mudar para gastar melhor. Sem precisar virar especialista.
          </Reveal>
          <Reveal delay={850} className="sh-ctas">
            <Link to="/cadastro" className="sh-btn sh-btn-cta">Testar {TRIAL_DAYS} dias grátis <ArrowRight size={16} /></Link>
            <Link to="/como-funciona" className="sh-btn sh-btn-ghost">Ver como funciona</Link>
          </Reveal>
          <Reveal as="p" delay={950} className="sh-fine">Sem cartão para começar. O dinheiro dos anúncios continua indo direto para o Google, a Meta e o TikTok.</Reveal>
        </div>
        <Reveal delay={300}><HeroFilm /></Reveal>
      </div>
    </section>
  )
}

function Marquee() {
  const row = PLATFORMS.map((p) => <span key={p} className="sh-mq-item">{p}</span>)
  return (
    <section className="sh-mq" aria-label={`Funciona com ${PLATFORMS.join(', ')}`}>
      <span className="sh-label sh-mq-k">Funciona com</span>
      <div className="sh-mq-track" aria-hidden><div className="sh-mq-run">{row}{row}</div></div>
    </section>
  )
}

/** Frase que acende quando passa pelo meio da tela e continua acesa. */
function LitLine({ children }: { children: string }) {
  const [ref, inView] = useInView<HTMLParagraphElement>({ rootMargin: '0px 0px -45% 0px' })
  return <p ref={ref} className={`sh-lit ${inView ? 'is-in' : ''}`}>{children}</p>
}

function Problem() {
  const pains: Array<[typeof PiggyBank, string, string]> = [
    [PiggyBank, 'Dinheiro indo embora', 'Anúncio rodando de madrugada, gente procurando “receita de bolo” clicando no seu anúncio, campanha boa sem verba enquanto a ruim gasta.'],
    [BarChart3, 'Números que não dizem nada', 'CPC, CTR, CPM… O que importa é: quantas pessoas me chamaram e quanto eu vendi.'],
    [Clock, 'Sem tempo para cuidar', 'Você toca o negócio o dia inteiro. Abrir o gerenciador de anúncios toda semana para ajustar campanha acaba ficando para depois.'],
  ]
  return (
    <section className="sh-violet">
      <div className="sh-wrap">
        <h2 className="sh-label">O problema</h2>
        <div className="sh-lits">
          <LitLine>Você sabe que precisa anunciar.</LitLine>
          <LitLine>O difícil é saber se está funcionando.</LitLine>
          <LitLine>E ninguém explica onde foi parar o dinheiro.</LitLine>
        </div>
        <div className="sh-pains">
          {pains.map(([Icon, t, d], i) => (
            <Reveal key={t} delay={i * 90} className="sh-pain">
              <Icon size={20} />
              <h3>{t}</h3>
              <p>{d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

function Steps() {
  const [ref, inView] = useInView<HTMLDivElement>()
  const steps: Array<[string, string]> = [
    ['Conte sobre o seu negócio', 'Ramo, cidade, objetivo (WhatsApp, site, loja ou ligações) e quanto pode investir por mês. Esse valor vira um teto que nunca é ultrapassado.'],
    ['Conecte suas contas', 'Pelas telas oficiais do Google, da Meta e do TikTok. Lemos até 12 meses do seu histórico para entender o que já funciona.'],
    ['Receba o diagnóstico', 'De 3 a 5 oportunidades concretas, com o motivo e exatamente o que muda. Você aplica com um clique ou deixa o piloto cuidar.'],
  ]
  return (
    <section className="sh-sec">
      <div className="sh-wrap">
        <p className="sh-label">Como funciona</p>
        <Words className="sh-display sh-h2" text="Do “não sei se funciona” a uma *lista do que fazer.*" />
        <div ref={ref} className={`sh-steps ${inView ? 'is-in' : ''}`}>
          <span className="sh-steps-line" aria-hidden />
          {steps.map(([t, d], i) => (
            <Reveal key={t} delay={150 + i * 160} className="sh-step">
              <span className="sh-step-n">0{i + 1}</span>
              <h3>{t}</h3>
              <p>{d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

function Features() {
  const small: Array<[typeof LineChart, string, string]> = [
    [LineChart, 'Painel que fala a sua língua', 'Investido, clientes, custo por cliente e retorno estimado, com a explicação de cada número.'],
    [Lightbulb, 'Sugestões com o porquê', 'Cada sugestão mostra o motivo, o impacto estimado e o que muda (de → para) antes de você aplicar.'],
    [Wand2, 'Criação guiada de campanhas', 'Diga o que quer vender; montamos a campanha no Google e na Meta, com textos sugeridos por IA para você revisar.'],
    [Radar, 'Medição do site sem dor de cabeça', 'Passo a passo para o seu site (Wix, WordPress ou hospedagem própria) contar quem virou cliente.'],
    [LayoutTemplate, 'Páginas de divulgação prontas', 'Não tem site? Monte uma página no editor, já medida, com o seu domínio. Adicional na assinatura.'],
    [Globe, 'Analytics do site', 'Conecte o Google Analytics e veja visitas, origem e conversões do site no mesmo painel.'],
  ]
  return (
    <section className="sh-sec sh-sec-alt">
      <div className="sh-wrap">
        <p className="sh-label">O que vem junto</p>
        <Words className="sh-display sh-h2" text="Tudo o que você precisa, em *linguagem de negócio.*" />
        <div className="sh-bento">
          <Reveal className="sh-card sh-card-big" style={{ gridArea: 'pilot' }}>
            <div className="sh-card-in" onPointerMove={trackGlow}>
              <Bot size={20} className="sh-card-ic" />
              <h3>Piloto automático com limites</h3>
              <p>Escolha entre só sugerir, copiloto ou automático. Os limites que você define são sempre respeitados.</p>
              <div className="sh-limits" aria-hidden>
                {['Teto mensal', 'Custo máximo por cliente', 'Variação diária'].map((l, i) => (
                  <div key={l} className="sh-limit" style={{ '--s': i } as React.CSSProperties}><span>{l}</span><i><b /></i></div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal className="sh-card sh-card-big" delay={90} style={{ gridArea: 'undo' }}>
            <div className="sh-card-in" onPointerMove={trackGlow}>
              <Undo2 size={20} className="sh-card-ic" />
              <h3>Histórico e desfazer</h3>
              <p>Tudo o que mudou fica registrado: quem mudou, quando e por quê. E dá para desfazer.</p>
              <ul className="sh-log" aria-hidden>
                {[['Orçamento de “Bolos de festa”', 'Você'], ['Pausou palavra “receita de bolo”', 'Piloto'], ['Novo anúncio no Instagram', 'Você']].map(([t, who], i) => (
                  <li key={t} style={{ '--s': i } as React.CSSProperties}><Check size={13} strokeWidth={3} /><span>{t}</span><em>{who}</em><Undo2 size={13} /></li>
                ))}
              </ul>
            </div>
          </Reveal>
          {small.map(([Icon, t, d], i) => (
            <Reveal key={t} className="sh-card" delay={(i % 3) * 90}>
              <div className="sh-card-in" onPointerMove={trackGlow}>
                <Icon size={20} className="sh-card-ic" />
                <h3>{t}</h3>
                <p>{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

function Numbers() {
  return (
    <section className="sh-sec">
      <div className="sh-wrap sh-nums">
        <Reveal className="sh-stat"><b><Counter value={12} /> meses</b><span>de histórico lidos ao conectar</span></Reveal>
        <Reveal className="sh-stat" delay={90}><b>3 a 5</b><span>oportunidades no primeiro diagnóstico</span></Reveal>
        <Reveal className="sh-stat" delay={180}><b><Counter value={TRIAL_DAYS} /> dias</b><span>grátis, sem cartão</span></Reveal>
        <Reveal className="sh-stat" delay={270}><b>R$ <Counter value={0} /></b><span>de taxa sobre o que você investe em anúncios</span></Reveal>
      </div>
    </section>
  )
}

function Agencies() {
  return (
    <section className="sh-light">
      <div className="sh-wrap sh-split">
        <div>
          <p className="sh-label">Também para agências e gestores de tráfego</p>
          <Words className="sh-display sh-h2" text="Feito para pequenos negócios. *Pronto para agências.*" />
          <Reveal as="p" className="sh-p">O Seu Impulso nasceu para o dono do pequeno negócio, mas agências e gestores de tráfego também podem criar e gerenciar todos os clientes num lugar só: a saúde de cada conta, o gasto do mês e as sugestões pendentes, com portal do cliente, relatórios com a sua marca e link para o cliente conectar as contas sem precisar de login.</Reveal>
          <Reveal delay={120}><Link to="/agencias" className="sh-btn sh-btn-dark">Conhecer o plano para agências <ArrowRight size={16} /></Link></Reveal>
        </div>
        <Reveal delay={150} className="sh-checks">
          {['Crie e gerencie todos os seus clientes numa conta só', 'Cada cliente com as próprias conexões, isoladas', 'Papéis por pessoa: gestor, analista, cliente, leitor', 'Cliente pode aprovar sugestões, se você permitir', 'Cobrança por empresa ativa'].map((t, i) => (
            <div key={t} style={{ '--s': i } as React.CSSProperties}><Check size={16} strokeWidth={3} /> {t}</div>
          ))}
        </Reveal>
      </div>
    </section>
  )
}

function Questions() {
  return (
    <section className="sh-sec sh-sec-alt">
      <div className="sh-wrap sh-split">
        <div>
          <p className="sh-label">Perguntas frequentes</p>
          <Words className="sh-display sh-h2" text="Antes de *começar.*" />
          <Reveal className="sh-note"><ShieldCheck size={18} /><span>O dinheiro dos anúncios não passa por nós. Você paga direto às plataformas; o Seu Impulso cobra só a assinatura.</span></Reveal>
        </div>
        <Reveal delay={120} className="sh-faq">
          <Faq items={[
            ['Preciso entender de anúncios para usar?', 'Não. O Seu Impulso foi feito para quem não é especialista. Tudo é explicado em português simples, e os termos técnicos ficam escondidos para quem quiser ver.'],
            ['O Seu Impulso mexe nos meus anúncios sozinho?', 'Só se você quiser. Por padrão ele sugere e você aplica. No modo Copiloto ou Automático ele age dentro dos limites que você definir, e tudo pode ser desfeito.'],
            ['Quanto preciso investir em anúncios?', 'Funciona bem a partir de uns R$ 300 por mês. Você define o teto e ele nunca é ultrapassado.'],
            ['E se eu ainda não tenho conta de anúncios?', 'Sem problema. Você pode começar mesmo assim e conectar depois; mostramos o caminho para criar a conta no Google e na Meta.'],
            ['Meus dados ficam seguros?', 'As conexões usam os acessos oficiais das plataformas, os tokens ficam criptografados e cada empresa é isolada. Você pode desconectar e pedir a exclusão dos dados quando quiser.'],
            ['Sou agência. Posso usar com meus clientes?', 'Pode. Existe um plano para agências: você cria e gerencia todos os clientes num lugar só, com papéis para a equipe, portal do cliente e relatórios com a sua marca.'],
            [`O que acontece depois dos ${TRIAL_DAYS} dias?`, 'Você escolhe um plano e a forma de pagamento (Pix, boleto ou cartão). Se não escolher, a conta fica pausada, sem cobrança.'],
          ]} />
        </Reveal>
      </div>
    </section>
  )
}

function FinalCta() {
  return (
    <section className="sh-final">
      <div className="sh-glow sh-glow-final" aria-hidden />
      <div className="sh-wrap">
        <Words className="sh-display sh-final-h" text="Comece hoje. *Sem cartão.*" />
        <Reveal as="p" delay={300} className="sh-lead">{TRIAL_DAYS} dias grátis com diagnóstico completo das suas contas. Cancele quando quiser.</Reveal>
        <Reveal delay={420} className="sh-ctas sh-ctas-center">
          <Link to="/cadastro" className="sh-btn sh-btn-cta">Testar grátis <ArrowRight size={16} /></Link>
          <Link to="/precos" className="sh-btn sh-btn-ghost">Ver preços</Link>
        </Reveal>
      </div>
    </section>
  )
}
